import { IRoute } from '@/hooks/useMenu'
import React, { useEffect, useRef, useState } from 'react'
import { MdHistory, MdPushPin, MdSearch } from 'react-icons/md'

import { MenuFlyout } from './flyout'
import { Rail } from './styles'
import { IMenuLeaf } from './utils'

type FlyoutSource = 'pinned' | 'recent' | 'search' | string

interface IOpenFlyout {
  /** 'pinned', 'recent', 'search' ou a rota do grupo. */
  source: FlyoutSource
  title: string
  items: IMenuLeaf[]
  /** Distância do topo da janela até o ícone que abriu o cartão. */
  anchorTop: number
}

interface IProps {
  config: IRoute[]
  pinnedItems: IMenuLeaf[]
  recentItems: IMenuLeaf[]
  activeRoute: string | null
  searchTerm: string
  onSearchChange: (value: string) => void
  searchResults: IMenuLeaf[]
  onNavigate: (route: string) => void
  onReorderPinned: (from: number, to: number) => void
  onToggleMenu: () => void
}

/**
 * Menu recolhido: uma coluna de 60px dividida em zonas (busca, fixados, grupos, recentes), onde
 * toda lista abre no mesmo cartão flutuante. A ideia é não esconder nada nem deixar o rail
 * crescer — por isso os fixados moram sob um único alfinete, com a contagem no badge.
 *
 * O cartão abre no clique, nunca ao passar o mouse: com os ícones empilhados, abrir por hover
 * fazia o cartão aparecer sozinho enquanto o ponteiro só cruzava o rail. No hover fica só o
 * tooltip com o nome.
 */
export const MenuRail: React.FC<IProps> = ({
  config,
  pinnedItems,
  recentItems,
  activeRoute,
  searchTerm,
  onSearchChange,
  searchResults,
  onNavigate,
  onReorderPinned,
  onToggleMenu
}) => {
  const [flyout, setFlyout] = useState<IOpenFlyout | null>(null)

  const railRef = useRef<HTMLDivElement>(null)

  const close = () => setFlyout(null)

  // Rolagem, redimensionamento e clique fora tirariam o cartão do lugar — fecha antes disso.
  useEffect(() => {
    if (!flyout) {
      return
    }

    function handleClickOutside(event: MouseEvent) {
      const target = event.target as HTMLElement

      if (railRef.current?.contains(target)) {
        return
      }

      if (target.closest?.('[data-menu-flyout]')) {
        return
      }

      close()
    }

    // * O listener é em fase de captura para pegar a rolagem de qualquer container, não só a da
    // * janela — o que fecharia o cartão à toa quando a rolagem é a da lista dele mesmo. Só a
    // * rolagem de fora conta: essa sim tira o ícone de baixo do cartão.
    function handleScroll(event: Event) {
      const target = event.target as HTMLElement | null

      if (target?.closest?.('[data-menu-flyout]')) {
        return
      }

      close()
    }

    window.addEventListener('scroll', handleScroll, true)
    window.addEventListener('resize', close)
    document.addEventListener('mousedown', handleClickOutside)

    return () => {
      window.removeEventListener('scroll', handleScroll, true)
      window.removeEventListener('resize', close)
      document.removeEventListener('mousedown', handleClickOutside)
    }
  }, [flyout])

  function sourceOf(source: FlyoutSource) {
    if (source === 'pinned') {
      return { title: 'FIXADOS', items: pinnedItems }
    }

    if (source === 'recent') {
      return { title: 'RECENTES', items: recentItems }
    }

    if (source === 'search') {
      return { title: 'BUSCAR', items: searchResults }
    }

    const group = config.find(route => route.route === source)

    return {
      title: group?.title || '',
      // O terceiro nível é achatado: no cartão não há espaço para um submenu dentro do submenu.
      items: (group?.options || [])
        .flatMap(option =>
          option.options?.length ? option.options : [option as any]
        )
        .filter(option => option.route)
        .map(option => ({
          route: option.route,
          title: option.title,
          groupTitle: group?.title || '',
          groupRoute: group?.route || ''
        }))
    }
  }

  function toggle(source: FlyoutSource, target: HTMLElement) {
    if (flyout?.source === source) {
      close()
      return
    }

    setFlyout({
      source,
      ...sourceOf(source),
      anchorTop: target.getBoundingClientRect().top
    })
  }

  function targetProps(source: FlyoutSource, label: string) {
    const isOpen = flyout?.source === source

    return {
      type: 'button' as const,
      className: `rail_target ${isOpen ? 'is-open' : ''}`,
      'aria-label': label,
      'aria-haspopup': 'menu' as const,
      'aria-expanded': isOpen,
      onClick: (e: React.MouseEvent<HTMLButtonElement>) =>
        toggle(source, e.currentTarget),
      onKeyDown: (e: React.KeyboardEvent<HTMLButtonElement>) => {
        // Enter já dispara o clique; a seta direita é o atalho equivalente para abrir.
        if (e.key !== 'ArrowRight') {
          return
        }
        e.preventDefault()
        if (flyout?.source !== source) {
          toggle(source, e.currentTarget)
        }
      }
    }
  }

  return (
    <>
      <Rail ref={railRef} className="menu_rail">
        <button
          type="button"
          className="rail_target"
          aria-label="Expandir menu"
          aria-expanded={false}
          onClick={onToggleMenu}
        >
          <span className="rail_icon rail_burger">
            <span />
            <span />
            <span />
          </span>
          <span className="rail_tooltip">Expandir menu</span>
        </button>

        <button {...targetProps('search', 'Buscar no menu')}>
          <MdSearch className="rail_icon" />
          <span className="rail_tooltip">Buscar no menu</span>
        </button>

        <div className="rail_divider" />

        {pinnedItems.length ? (
          <>
            <button
              {...targetProps('pinned', `Fixados (${pinnedItems.length})`)}
              className={`rail_target is-pinned-target ${
                flyout?.source === 'pinned' ? 'is-open' : ''
              }`}
            >
              <MdPushPin className="rail_icon is-pinned" />
              <span className="rail_badge">{pinnedItems.length}</span>
              <span className="rail_tooltip">
                Fixados ({pinnedItems.length})
              </span>
            </button>
            <div className="rail_divider" />
          </>
        ) : null}

        {config?.map(route => (
          <button key={route.route} {...targetProps(route.route, route.title)}>
            <span className="rail_icon">{route.icon}</span>
            <span className="rail_tooltip">{route.title}</span>
          </button>
        ))}

        {recentItems.length ? (
          <>
            <div className="rail_divider" />
            <button {...targetProps('recent', 'Recentes')}>
              <MdHistory className="rail_icon is-recent" />
              <span className="rail_tooltip">Recentes</span>
            </button>
          </>
        ) : null}
      </Rail>

      {flyout ? (
        <MenuFlyout
          title={flyout.title}
          anchorTop={flyout.anchorTop}
          items={
            flyout.source === 'search'
              ? searchResults
              : sourceOf(flyout.source).items
          }
          activeRoute={activeRoute}
          withPinIcon={flyout.source === 'pinned'}
          searchable={flyout.source === 'search'}
          searchTerm={searchTerm}
          onSearchChange={onSearchChange}
          reorderable={flyout.source === 'pinned'}
          onReorder={onReorderPinned}
          onNavigate={route => {
            close()
            onNavigate(route)
          }}
          onClose={close}
        />
      ) : null}
    </>
  )
}
